import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import clsx from 'clsx';

import { Container } from '../../Container/Container';
import BackButton from '../../BackButton/BackButton';
import Card from '../CatalogPage/components/List/Card/Card';

import Print from '../../Icons/Print';
import Plus from '../../Icons/Plus';
import Minus from '../../Icons/Minus';

import { getProduct } from '../../../api/product/getProduct';
import { IGetProductResponse } from '../../../types/products';
import { getDocumentUrl } from '../../../api/document/getDocumentUrl';
import { getDocumentJpgUrl } from '../../../api/document/getDocumentJpgUrl';

import 'react-pdf/dist/Page/TextLayer.css';
import styles from './SchemePage.module.scss';
import Search from '../../Icons/Search';

import { getFavorites, updateFavorites } from '../../../helpers/favorites';
import { ICardProps as IFavorites } from '../FavoritesPage/components/Card/Card';

import Item from './components/Item/Item';

import Favorites from '../../Icons/Favorites';

import ReactPdfDocument from './components/ReactPdfDocument/ReactPdfDocument';
import PdfViewer from './components/PdfViewer/PdfViewer';
import ErrorBoundary from '../../ErrorBoundary/ErrorBoundary';

export interface IPart {
  id: string;
  cmmf: string;
  name: string;
  type: string;
  partCode: string;
  image: {
    s3Key: string;
    s3Bucket: string;
    size: number;
    mimeType: string;
  };
  status: string;
  count: number;
  slug: string;
}

const SchemePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [product, setProduct] = useState<IGetProductResponse | null>(null);
  const [documentLink, setDocumentLink] = useState<string | null>(null);
  const [documentImageLink, setDocumentImageLink] = useState<string>('');
  const [nomenclatureOpen, setNomenclatureOpen] = useState(false);
  const [activeDetail, setActiveDetail] = useState<any>();
  const [filteredDetails, setFilteredDetails] = useState<IPart[]>([]);
  const [details, setDetails] = useState<IPart[]>([]);
  const [favorites, setFavorites] = useState<IFavorites[]>([]);

  const searchFunc = (str: string) => {
    if (str !== '') {
      const arr =
        details.filter((item) => {
          if (
            item.cmmf.toLowerCase().indexOf(str) !== -1 ||
            item.partCode?.toLowerCase().indexOf(str) !== -1 ||
            item.name.toLowerCase().indexOf(str) !== -1
          )
            return item;
        }) || [];
      setFilteredDetails(arr);
    } else {
      setFilteredDetails(details);
    }
  };

  const [indexInfavorites, setIndexInFavorites] = useState<number>(-1);

  const updateFavoritesList = (data: IFavorites) => {
    if (indexInfavorites === -1) {
      updateFavorites(data, 'add').then(() => {
        setIndexInFavorites(1);
      });
    } else
      updateFavorites(data, 'delete').then(() => {
        setIndexInFavorites(-1);
      });
  };

  useEffect(() => {
    getProduct(id as string)
      .then((res) => {
        setProduct(res);
        setFilteredDetails(
          res.parts.map((item) => ({ ...item.displayData, id: item.id })),
        );
        setDetails(
          res.parts.map((item) => ({ ...item.displayData, id: item.id })),
        );
      })
      .catch(() => {
        navigate('/not-found');
      });
    getFavorites().then((arr) => setFavorites(arr));
  }, []);

  useEffect(() => {
    if (activeDetail) setNomenclatureOpen(true);
    console.log('activeDetail', activeDetail);
  }, [activeDetail]);

  useEffect(() => {
    if (!product) return;
    if (
      product.explodedViews[0] &&
      product.explodedViews[0].s3Bucket &&
      product.explodedViews[0].s3Key
    ) {
      getDocumentUrl(
        product.explodedViews[0].s3Bucket,
        product.explodedViews[0].s3Key,
      ).then((res) => {
        setDocumentLink(res.url);
      });
      setDocumentImageLink(
        getDocumentJpgUrl(
          product.explodedViews[0].s3Bucket,
          product.explodedViews[0].s3Key,
        ),
      );
    }
  }, [product]);

  return (
    <div className={styles.SchemePage}>
      {product && documentLink ? (
        <>
          <Container className={styles.SchemePage_container}>
            <div className={styles.header}>
              <BackButton link={'/product/' + product.id} />
              <div className={styles.content}>
                <h1>{product.name}</h1>
                <div className={styles.buttons}>
                  <a
                    href={documentLink}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.print}
                  >
                    <Print />
                    Печать (PDF)
                  </a>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      updateFavoritesList({
                        type: 'product',
                        name: product.name,
                        cmmf: product.cmmf,
                        brand: product.brand,
                        category: product.category,
                        image: product.image,
                        article_number: '',
                        commercial_number: product.cmmf,
                        id: product.id,
                      });
                    }}
                  >
                    <Favorites active={indexInfavorites !== -1} />
                    {indexInfavorites !== -1 ? 'В избранном' : 'В избранное'}
                  </button>
                </div>
              </div>
            </div>
            <ErrorBoundary
              fallback={<PdfViewer documentLink={documentImageLink} />}
            >
              <ReactPdfDocument
                documentLink={documentLink}
                details={details}
                product={product}
                setFilteredDetails={setFilteredDetails}
                setDetails={setDetails}
                setActiveDetail={setActiveDetail}
              />
            </ErrorBoundary>
          </Container>
          <div
            className={clsx(styles.nomenclature, {
              [styles.open]: nomenclatureOpen,
            })}
          >
            <Container>
              <div className={styles.button_holder}>
                <button
                  onClick={() =>
                    !nomenclatureOpen
                      ? setNomenclatureOpen(true)
                      : setNomenclatureOpen(false)
                  }
                >
                  Номенклатура
                  <span>{!nomenclatureOpen ? <Plus /> : <Minus />}</span>
                </button>
              </div>
              {nomenclatureOpen ? (
                activeDetail ? (
                  <>
                    <div className={styles.detail}>
                      <Card
                        favorites={favorites}
                        data={{
                          article_number:
                            activeDetail.partCode || activeDetail.cmmf,
                          cmmf: activeDetail.partCode || activeDetail.cmmf,
                          commercial_number: activeDetail.code,
                          name: activeDetail.name,
                          brand: { name: '-' },
                          category: { name: '-' },
                          status: activeDetail.status || null,
                          image: activeDetail.image,
                          replaceBy: [],
                          id: activeDetail.id,
                          type: 'part',
                          in_stoke:
                            activeDetail.count && activeDetail.count > 0
                              ? true
                              : false,
                          count: activeDetail.count || null,
                          slug: activeDetail.slug,
                        }}
                      />
                    </div>
                    <div className={styles.all_items}>
                      <button onClick={() => setActiveDetail(null)}>
                        Весь список деталей
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.search_holder}>
                      <input
                        name="search"
                        placeholder="Поиск по коду или названию"
                        onInput={(e) =>
                          searchFunc((e.target as HTMLTextAreaElement).value)
                        }
                      />
                      <Search />
                    </div>
                    <div className={styles.details}>
                      {filteredDetails.map((item, index) => (
                        <Item
                          key={index}
                          id={item.id}
                          article_number={item.partCode}
                          cmmf={item.cmmf}
                          name={item.name}
                          status={item.status || undefined}
                          type="part"
                          commercial_number="1234567"
                          className={styles.detail_item}
                          slug={item.slug}
                        />
                      ))}
                    </div>
                  </>
                )
              ) : null}
            </Container>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default SchemePage;
